/**
 * Service package rate-card math (mirrors backend packageRates.js).
 *
 * Unit-aware multiplier:
 *   minute → qty
 *   hour   → qty × 60  (rates are per minute)
 *   flat   → 1
 *   other  → qty
 *
 * bill = package_price / multi
 * total_pay = pay × multi   (e.g. 12 × 600 min = 7200; 12 × 1 hr = 720)
 * total_spread = spread × multi
 */

export function toFiniteNumber(value, fallback = 0) {
  if (value == null || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function roundMoney(n, digits = 2) {
  const f = 10 ** digits;
  return Math.round((Number(n) || 0) * f) / f;
}

export function roundPct(n, digits = 2) {
  return roundMoney(n, digits);
}

export function normalizeDurationUnit(unit) {
  return String(unit || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

export function durationMultiplier(quantity, unit) {
  const qty = Math.max(0, toFiniteNumber(quantity));
  const u = normalizeDurationUnit(unit);

  if (u === 'minute' || u === 'min' || u === 'minutes' || u === 'mins') {
    return {
      multi: qty,
      basis: 'minute',
      rateLabel: '/min',
      multiLabel: qty === 1 ? '1 min' : `${qty} min`,
      formulaParts: qty > 0 ? `× ${qty} min` : '',
    };
  }

  if (u === 'hour' || u === 'hours' || u === 'hr' || u === 'hrs') {
    const multi = qty * 60;
    return {
      multi,
      basis: 'minute',
      rateLabel: '/min',
      multiLabel: qty === 1 ? '1 hr (60 min)' : `${qty} hr (${multi} min)`,
      formulaParts: multi > 0 ? `× ${multi} min` : '',
    };
  }

  if (u === 'flat' || u === 'fixed' || u === 'package' || u === 'lump' || u === 'lump_sum') {
    return {
      multi: qty > 0 ? 1 : 0,
      basis: 'flat',
      rateLabel: 'flat',
      multiLabel: 'flat',
      formulaParts: '× 1',
    };
  }

  const label = u || 'unit';
  return {
    multi: qty,
    basis: 'unit',
    rateLabel: `/${label}`,
    multiLabel: qty === 1 ? `1 ${label}` : `${qty} ${label}`,
    formulaParts: qty > 0 ? `× ${qty} ${label}` : '',
  };
}

export function billRateFromPackage(priceInput, durationInput, unitInput) {
  const price = toFiniteNumber(priceInput);
  const { multi } = durationMultiplier(durationInput, unitInput);
  if (multi <= 0 || price < 0) return null;
  return roundMoney(price / multi);
}

export function ratesFromPayBill(payInput, billInput) {
  const pay_rate = roundMoney(Math.max(0, toFiniteNumber(payInput)));
  const bill_rate = roundMoney(Math.max(0, toFiniteNumber(billInput)));
  const spread = roundMoney(bill_rate - pay_rate);
  const gross_margin_pct = bill_rate > 0 ? roundPct((spread / bill_rate) * 100) : 0;
  const markup_pct = pay_rate > 0 ? roundPct((spread / pay_rate) * 100) : 0;
  return { pay_rate, bill_rate, spread, gross_margin_pct, markup_pct };
}

export function ratesFromPayAndMargin(payInput, marginInput) {
  const pay_rate = roundMoney(Math.max(0, toFiniteNumber(payInput)));
  let gross_margin_pct = toFiniteNumber(marginInput);
  if (gross_margin_pct >= 100) gross_margin_pct = 99.99;
  if (gross_margin_pct < 0) gross_margin_pct = 0;
  gross_margin_pct = roundPct(gross_margin_pct);
  const denom = 1 - gross_margin_pct / 100;
  const bill_rate =
    denom > 0 && pay_rate > 0 ? roundMoney(pay_rate / denom) : pay_rate > 0 ? pay_rate : 0;
  return ratesFromPayBill(pay_rate, bill_rate);
}

export function ratesFromBillAndMargin(billInput, marginInput) {
  const bill_rate = roundMoney(Math.max(0, toFiniteNumber(billInput)));
  let gross_margin_pct = toFiniteNumber(marginInput);
  if (gross_margin_pct >= 100) gross_margin_pct = 99.99;
  if (gross_margin_pct < 0) gross_margin_pct = 0;
  gross_margin_pct = roundPct(gross_margin_pct);
  const pay_rate = roundMoney(bill_rate * (1 - gross_margin_pct / 100));
  return ratesFromPayBill(pay_rate, bill_rate);
}

export function packageTotals(payRate, billRate, durationQty, durationUnit, load = {}) {
  const rates = ratesFromPayBill(payRate, billRate);
  const dm = durationMultiplier(durationQty, durationUnit);
  const multi = dm.multi;
  const total_pay = roundMoney(rates.pay_rate * multi);
  const total_bill = roundMoney(rates.bill_rate * multi);
  const total_spread = roundMoney(rates.spread * multi); // bill − pay only
  const loaded = applyLoadCosts(total_pay, total_bill, multi, rates, load);
  return {
    ...rates,
    multi,
    basis: dm.basis,
    rate_label: dm.rateLabel,
    multi_label: dm.multiLabel,
    formula_parts: dm.formulaParts,
    total_pay,
    total_bill,
    total_spread,
    ...loaded,
  };
}

/**
 * Burden & Overhead (OpEx): each can be % of total pay, or a direct currency amount (line total).
 * Loaded cost = pay + burden + overhead
 * Contribution = bill − loaded cost
 */
export function normalizeCostMode(mode) {
  const m = String(mode || 'pct').trim().toLowerCase();
  if (m === 'amount' || m === 'currency' || m === 'money' || m === 'absolute' || m === 'flat') {
    return 'amount';
  }
  return 'pct';
}

export function computeCostAmount(mode, value, baseTotal) {
  const v = Math.max(0, toFiniteNumber(value));
  if (normalizeCostMode(mode) === 'amount') return roundMoney(v);
  return roundMoney((Math.max(0, toFiniteNumber(baseTotal)) * v) / 100);
}

export function applyLoadCosts(totalPay, totalBill, multi, unitRates = {}, load = {}) {
  const total_pay = roundMoney(totalPay);
  const total_bill = roundMoney(totalBill);
  const burden_mode = normalizeCostMode(load.burden_mode ?? load.burdenMode);
  const overhead_mode = normalizeCostMode(load.overhead_mode ?? load.overheadMode);
  const burden_value = Math.max(0, toFiniteNumber(load.burden_value ?? load.burdenValue));
  const overhead_value = Math.max(0, toFiniteNumber(load.overhead_value ?? load.overheadValue));

  const total_burden = computeCostAmount(burden_mode, burden_value, total_pay);
  const total_overhead = computeCostAmount(overhead_mode, overhead_value, total_pay);
  const total_loaded_cost = roundMoney(total_pay + total_burden + total_overhead);
  const total_contribution = roundMoney(total_bill - total_loaded_cost);
  const contribution_margin_pct =
    total_bill > 0 ? roundPct((total_contribution / total_bill) * 100) : 0;

  const m = multi > 0 ? multi : 0;
  const burden_rate = m > 0 ? roundMoney(total_burden / m) : total_burden;
  const overhead_rate = m > 0 ? roundMoney(total_overhead / m) : total_overhead;
  const loaded_rate = roundMoney(
    (unitRates.pay_rate || 0) + burden_rate + overhead_rate
  );
  const contribution_rate = roundMoney((unitRates.bill_rate || 0) - loaded_rate);

  return {
    burden_mode,
    burden_value,
    overhead_mode,
    overhead_value,
    total_burden,
    total_overhead,
    total_loaded_cost,
    total_contribution,
    contribution_margin_pct,
    burden_rate,
    overhead_rate,
    loaded_rate,
    contribution_rate,
  };
}

/**
 * Apply package form field change (+ duration unit).
 * Always recalculates bill_rate from package_price ÷ duration multi.
 * Optionally syncs entitlement line bill rates for aligned units (keep line pay).
 */
export function applyRateFormChange(prev, field, rawValue) {
  const next = { ...prev, [field]: rawValue };
  const packageBill = billRateFromPackage(
    next.package_price,
    next.service_duration,
    next.service_duration_unit
  );
  const dm = durationMultiplier(next.service_duration, next.service_duration_unit);
  const multi = dm.multi;
  const bill = packageBill != null && multi > 0 ? packageBill : 0;

  // Package form no longer collects package-level pay; use 0 for package totals frame.
  const pay = toFiniteNumber(next.consultant_hourly_rate);
  const rates = ratesFromPayBill(pay, bill);

  let items = Array.isArray(next.items) ? next.items : [];
  if (
    field === 'package_price' ||
    field === 'service_duration' ||
    field === 'service_duration_unit'
  ) {
    items = syncEntitlementBillFromPackage(items, bill, next.service_duration_unit);
  }

  const totals = packageTotals(rates.pay_rate, rates.bill_rate, next.service_duration, next.service_duration_unit);

  return {
    ...next,
    items,
    consultant_bill_rate: rates.bill_rate,
    gross_margin_pct: rates.gross_margin_pct,
    _spread: rates.spread,
    _markup_pct: rates.markup_pct,
    _total_pay: totals.total_pay,
    _total_bill: totals.total_bill,
    _total_spread: totals.total_spread,
    _rate_label: totals.rate_label,
    _multi: totals.multi,
    _multi_label: totals.multi_label,
  };
}

/** Minute/hour units share package bill basis; same unit codes match exactly. */
export function isEntitlementBillAligned(itemUnit, packageUnit) {
  const a = normalizeDurationUnit(itemUnit);
  const b = normalizeDurationUnit(packageUnit);
  if (!a || !b) return false;
  const minuteLike = (u) =>
    u === 'minute' ||
    u === 'min' ||
    u === 'minutes' ||
    u === 'mins' ||
    u === 'hour' ||
    u === 'hours' ||
    u === 'hr' ||
    u === 'hrs';
  if (minuteLike(a) && minuteLike(b)) return true;
  return a === b;
}

/** Push package bill rate onto aligned entitlement lines; recompute spread from line pay. */
export function syncEntitlementBillFromPackage(items, packageBillRate, packageUnit) {
  const bill = roundMoney(Math.max(0, toFiniteNumber(packageBillRate)));
  return (items || []).map((item) => {
    if (!isEntitlementBillAligned(item.unit, packageUnit)) return item;
    const pay = toFiniteNumber(item.pay_rate);
    const rates = ratesFromPayBill(pay, bill);
    const totals = packageTotals(rates.pay_rate, rates.bill_rate, item.quantity, item.unit, {
      burden_mode: item.burden_mode,
      burden_value: item.burden_value,
      overhead_mode: item.overhead_mode,
      overhead_value: item.overhead_value,
    });
    return {
      ...item,
      bill_rate: rates.bill_rate,
      pay_rate: rates.pay_rate,
      gross_margin_pct: rates.gross_margin_pct,
      spread: rates.spread,
      gross_amount: rates.spread,
      line_price: totals.total_bill,
      unit_price: rates.bill_rate,
      _rateLock: 'pay',
    };
  });
}

export function deriveRatesFromForm(form) {
  const packageBill = billRateFromPackage(
    form?.package_price,
    form?.service_duration,
    form?.service_duration_unit
  );
  const multi = durationMultiplier(form?.service_duration, form?.service_duration_unit).multi;
  const bill =
    packageBill != null && multi > 0 ? packageBill : form?.consultant_bill_rate;
  // Prefer rolled pay from time entitlement lines when package pay is unset.
  let pay = form?.consultant_hourly_rate;
  if (!(toFiniteNumber(pay) > 0) && Array.isArray(form?.items)) {
    const timeLine = form.items.find((i) =>
      isEntitlementBillAligned(i.unit, form.service_duration_unit)
    );
    if (timeLine) pay = timeLine.pay_rate;
  }
  return packageTotals(
    pay,
    bill,
    form?.service_duration,
    form?.service_duration_unit,
    {
      burden_mode: form?.burden_mode,
      burden_value: form?.burden_value,
      overhead_mode: form?.overhead_mode,
      overhead_value: form?.overhead_value,
    }
  );
}

export function packageRatesFromDetail(detail) {
  return deriveRatesFromForm({
    package_price: detail.package_price,
    service_duration: detail.service_duration,
    service_duration_unit: detail.service_duration_unit,
    consultant_hourly_rate: detail.consultant_pay_rate ?? detail.consultant_hourly_rate,
    consultant_bill_rate: detail.consultant_bill_rate,
    gross_margin_pct: detail.gross_margin_pct,
    burden_mode: detail.burden_mode,
    burden_value: detail.burden_value,
    overhead_mode: detail.overhead_mode,
    overhead_value: detail.overhead_value,
  });
}

/** Entitlement rates driven by bill + gross (pay derived). */
export function entitlementToRateInput(item = {}) {
  let bill = toFiniteNumber(item.bill_rate);
  if (!(bill > 0) && toFiniteNumber(item.line_price) > 0) {
    const fromLine = billRateFromPackage(item.line_price, item.quantity, item.unit);
    if (fromLine != null && fromLine > 0) bill = fromLine;
  }

  const grossMode = normalizeCostMode(item.gross_mode) === 'amount' ? 'amount' : 'pct';
  let pay = toFiniteNumber(item.pay_rate);
  let margin =
    item.gross_margin_pct != null && item.gross_margin_pct !== ''
      ? toFiniteNumber(item.gross_margin_pct)
      : null;

  if (bill > 0 && grossMode === 'amount') {
    // Gross $ = absolute spread (profit) per unit → pay = bill − amount
    const grossAmount =
      item.gross_amount != null && item.gross_amount !== ''
        ? Math.max(0, toFiniteNumber(item.gross_amount))
        : Math.max(0, toFiniteNumber(item.spread));
    pay = roundMoney(Math.max(0, bill - grossAmount));
    margin = ratesFromPayBill(pay, bill).gross_margin_pct;
  } else if (bill > 0 && margin != null) {
    const derived = ratesFromBillAndMargin(bill, margin);
    pay = derived.pay_rate;
    margin = derived.gross_margin_pct;
  } else if (bill > 0 && pay > 0) {
    margin = ratesFromPayBill(pay, bill).gross_margin_pct;
  } else if (bill > 0) {
    pay = 0;
    margin = margin ?? 0;
  } else {
    margin = margin ?? 0;
  }

  const rates = ratesFromPayBill(pay, bill);
  return {
    package_price: null,
    service_duration: item.quantity,
    service_duration_unit: item.unit,
    consultant_hourly_rate: rates.pay_rate,
    consultant_bill_rate: rates.bill_rate,
    gross_margin_pct: rates.gross_margin_pct,
    burden_mode: item.burden_mode || 'pct',
    burden_value: item.burden_value ?? 0,
    overhead_mode: item.overhead_mode || 'pct',
    overhead_value: item.overhead_value ?? 0,
    _rateLock: 'margin',
    _gross_mode: grossMode,
    _gross_amount: rates.spread,
  };
}

export function applyEntitlementRateChange(item, field, rawValue) {
  // Burden / overhead / gross mode or cost values: mode switch or numeric value only
  if (
    field === 'burden_mode' ||
    field === 'burden_value' ||
    field === 'overhead_mode' ||
    field === 'overhead_value' ||
    field === 'gross_mode'
  ) {
    const next = {
      ...item,
      [field]:
        field.endsWith('_mode') || field === 'gross_mode'
          ? normalizeCostMode(rawValue) === 'amount'
            ? 'amount'
            : 'pct'
          : rawValue,
    };
    // When switching Gross to $, seed the amount from current spread
    if (field === 'gross_mode' && next.gross_mode === 'amount') {
      const bill = toFiniteNumber(next.bill_rate);
      const margin = toFiniteNumber(next.gross_margin_pct);
      const fromPct = ratesFromBillAndMargin(bill, margin);
      next.gross_amount = fromPct.spread;
      next.pay_rate = fromPct.pay_rate;
      next.spread = fromPct.spread;
      next.gross_margin_pct = fromPct.gross_margin_pct;
    }
    if (field === 'gross_mode' && next.gross_mode === 'pct') {
      const bill = toFiniteNumber(next.bill_rate);
      const amount = Math.max(0, toFiniteNumber(next.gross_amount ?? next.spread));
      const pay = roundMoney(Math.max(0, bill - amount));
      const fromAmt = ratesFromPayBill(pay, bill);
      next.pay_rate = fromAmt.pay_rate;
      next.spread = fromAmt.spread;
      next.gross_margin_pct = fromAmt.gross_margin_pct;
    }
    return next;
  }

  // Spread is display-only — ignore edits
  if (field === 'spread') return item;

  const next = { ...item, [field]: rawValue };
  const qty = field === 'quantity' ? rawValue : next.quantity;
  const unit = field === 'unit' ? rawValue : next.unit;
  let bill = field === 'bill_rate' ? rawValue : next.bill_rate;
  const grossMode = normalizeCostMode(next.gross_mode) === 'amount' ? 'amount' : 'pct';
  const prevLock = next._rateLock || 'pay';

  let rates;
  // Employee pay rate is primary on entitlement lines
  if (field === 'pay_rate') {
    rates = ratesFromPayBill(rawValue, bill);
    next._rateLock = 'pay';
  } else if (field === 'bill_rate') {
    const pay = toFiniteNumber(next.pay_rate);
    if (pay > 0 && prevLock !== 'margin') {
      rates = ratesFromPayBill(pay, rawValue);
      next._rateLock = 'pay';
    } else if (grossMode === 'amount') {
      const grossAmount = Math.max(0, toFiniteNumber(next.gross_amount ?? next.spread));
      const newPay = roundMoney(Math.max(0, toFiniteNumber(rawValue) - grossAmount));
      rates = ratesFromPayBill(newPay, rawValue);
    } else {
      rates = ratesFromBillAndMargin(rawValue, next.gross_margin_pct);
      next._rateLock = 'margin';
    }
  } else if (grossMode === 'amount' && (field === 'gross_amount' || field === 'gross_margin_pct')) {
    const grossAmount = Math.max(0, toFiniteNumber(rawValue));
    const pay = roundMoney(Math.max(0, toFiniteNumber(bill) - grossAmount));
    rates = ratesFromPayBill(pay, bill);
    next.gross_amount = grossAmount;
    next._rateLock = 'margin';
  } else if (field === 'gross_margin_pct' || field === 'gross_amount') {
    rates = ratesFromBillAndMargin(bill, rawValue);
    next._rateLock = 'margin';
  } else {
    // unit / quantity: keep pay + bill, recompute totals
    rates = ratesFromPayBill(next.pay_rate, bill);
  }

  const totals = packageTotals(rates.pay_rate, rates.bill_rate, qty, unit, {
    burden_mode: next.burden_mode,
    burden_value: next.burden_value,
    overhead_mode: next.overhead_mode,
    overhead_value: next.overhead_value,
  });

  return {
    ...next,
    quantity: qty,
    unit,
    gross_mode: grossMode,
    bill_rate: rates.bill_rate,
    pay_rate: rates.pay_rate,
    gross_margin_pct: rates.gross_margin_pct,
    gross_amount: grossMode === 'amount' ? next.gross_amount ?? rates.spread : rates.spread,
    spread: rates.spread,
    line_price: totals.total_bill,
    unit_price: rates.bill_rate,
    _rateLock: next._rateLock || prevLock || 'pay',
  };
}

export function deriveEntitlementRates(item) {
  const input = entitlementToRateInput(item);
  return packageTotals(
    input.consultant_hourly_rate,
    input.consultant_bill_rate,
    input.service_duration,
    input.service_duration_unit,
    {
      burden_mode: input.burden_mode,
      burden_value: input.burden_value,
      overhead_mode: input.overhead_mode,
      overhead_value: input.overhead_value,
    }
  );
}

export function rollupEntitlementRates(items = []) {
  let total_pay = 0;
  let total_bill = 0;
  let total_spread = 0;
  let total_burden = 0;
  let total_overhead = 0;
  let total_loaded_cost = 0;
  let total_contribution = 0;
  for (const item of items || []) {
    const r = deriveEntitlementRates(item);
    total_pay += r.total_pay || 0;
    total_bill += r.total_bill || 0;
    total_spread += r.total_spread || 0;
    total_burden += r.total_burden || 0;
    total_overhead += r.total_overhead || 0;
    total_loaded_cost += r.total_loaded_cost || 0;
    total_contribution += r.total_contribution || 0;
  }
  return {
    total_pay: roundMoney(total_pay),
    total_bill: roundMoney(total_bill),
    total_spread: roundMoney(total_spread),
    total_burden: roundMoney(total_burden),
    total_overhead: roundMoney(total_overhead),
    total_loaded_cost: roundMoney(total_loaded_cost),
    total_contribution: roundMoney(total_contribution),
    contribution_margin_pct:
      total_bill > 0 ? roundPct((total_contribution / total_bill) * 100) : 0,
  };
}

